import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Mail, Phone, MapPin, Briefcase } from 'lucide-react';
import { getProfile } from '../../../services/dashboardService';
import { Card, Avatar } from '../styles';
import { formatDate } from '../../../utils/Utils';
import { formatPhone } from '../../../utils/phone';

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const BigAvatar = styled(Avatar)`
  width: 72px;
  height: 72px;
  font-size: 24px;
`;

const Name = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`;

const Company = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark?.medium || '#6B7280'};
  text-align: center;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark?.medium || '#6B7280'};
  width: 100%;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #F0F2F5;
  margin: 4px 0;
`;

export default function ProfileCard() {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    getProfile().then(res => setProfile(res.data?.data));
  }, []);

  if (!profile) return null;

  const initials = `${(profile.firstName || '')[0] || ''}${(profile.lastName || '')[0] || ''}`.toUpperCase();
  // ProfileResponse.Titles → List<ProfileTitleItem> { Id, Name }
  const titleName = profile.titles?.length > 0
    ? (profile.titles[0].name || profile.titles[0].titleName)
    : null;

  return (
    <Card $delay="0.1s">
      <ProfileWrapper>
        <BigAvatar>
          {profile.profilePicture ? <img src={profile.profilePicture} alt="" /> : initials}
        </BigAvatar>
        <Name>{profile.fullName || `${profile.firstName} ${profile.lastName}`}</Name>
        <Company>{profile.tenantName}</Company>
        {titleName && (
          <InfoRow>
            <Briefcase size={14} />
            <span>{titleName}</span>
          </InfoRow>
        )}
        <Divider />
        {profile.corporateEmail && (
          <InfoRow>
            <Mail size={14} />
            <span>{profile.corporateEmail}</span>
          </InfoRow>
        )}
        {profile.phoneNumber && (
          <InfoRow>
            <Phone size={14} />
            <span>{formatPhone(profile.areaCode, profile.phoneNumber, '')}</span>
          </InfoRow>
        )}
        {profile.employmentDate && (
          <InfoRow>
            <MapPin size={14} />
            <span>{t('dashboard.memberSince')} {formatDate(profile.employmentDate, i18n.language)}</span>
          </InfoRow>
        )}
      </ProfileWrapper>
    </Card>
  );
}
